import React from 'react';
import { Avatar, AvatarGroup, Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { CalendarBlank, Clock, HourglassMedium, Paperclip } from 'phosphor-react';
import { IEstatuto } from '../../../../hooks/useEstatuto';
import { IKanbanCard } from '../../../../hooks/useKanban';
import { SlaBadge } from '../../../../components/kanban/SlaBadge';
import { formatDateTimeBR } from '../utils';

interface EstatutoCardProps {
  cardTitulo: string;
  // Card do kanban (rico): responsáveis, supervisores, anexos, SLA. Plano B da Onda B —
  // enriquece o card próprio com os dados que já chegam no board, sem trocar o board/drawer.
  card?: IKanbanCard;
  estatuto?: IEstatuto;
  onClick?: () => void;
}

export const EstatutoCard: React.FC<EstatutoCardProps> = ({
  cardTitulo,
  card,
  estatuto,
  onClick,
}) => {
  const titulo = estatuto?.titulo || cardTitulo || (estatuto ? `Estatuto #${estatuto.id}` : '—');
  const isExpired = !!(
    estatuto?.expires_at && new Date(estatuto.expires_at).getTime() < Date.now()
  );
  const clickable = !!onClick;

  const responsaveis = card?.usuarios ?? [];
  const supervisores = card?.supervisores ?? [];
  const anexosCount = card?.anexos_count ?? 0;
  const hasFooter = responsaveis.length > 0 || supervisores.length > 0 || anexosCount > 0;

  return (
    <Box
      onClick={onClick}
      cursor={clickable ? 'pointer' : 'default'}
      bg="white"
      borderRadius="lg"
      p={3}
      borderWidth="1px"
      borderColor="gray.200"
      _hover={
        clickable
          ? {
              borderColor: 'blue.300',
              boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.08)',
              transform: 'translateY(-1px)',
            }
          : undefined
      }
      transition="all 0.15s ease"
    >
      <Flex justify="space-between" align="flex-start" gap={2} mb={2}>
        <Text fontSize="sm" fontWeight="600" color="gray.800" lineHeight="1.3" noOfLines={2}>
          {titulo}
        </Text>
        {/* SLA (T3) — semáforo por prazo. null = sem expira_em (não renderiza). */}
        {card?.sla && <SlaBadge sla={card.sla} />}
      </Flex>

      {/* T6.1: badge "Aguardando cliente" (destaque) — o motivo vai no tooltip */}
      {estatuto?.aguardando_cliente && (
        <Tooltip
          label={estatuto.motivo_bloqueio || 'Aguardando ação do cliente'}
          hasArrow
          placement="top"
        >
          <Flex
            align="center"
            gap={1}
            bg="orange.50"
            color="orange.700"
            borderRadius="md"
            px={1.5}
            py={0.5}
            mb={2}
            w="fit-content"
          >
            <HourglassMedium size={11} weight="fill" />
            <Text fontSize="10px" fontWeight="700">
              Aguardando cliente
            </Text>
          </Flex>
        </Tooltip>
      )}

      {estatuto && (
        <Flex direction="column" gap={1} mt={2}>
          <Flex align="center" gap={1.5}>
            <CalendarBlank size={12} color="#94A3B8" />
            <Text fontSize="10px" color="gray.500">
              Criado em {formatDateTimeBR(estatuto.dt_criacao)}
            </Text>
          </Flex>
          {estatuto.expires_at && (
            <Flex align="center" gap={1.5}>
              <Clock size={12} color={isExpired ? '#DC2626' : '#94A3B8'} />
              <Text fontSize="10px" color={isExpired ? 'red.500' : 'gray.500'}>
                {isExpired ? 'Expirou em' : 'Expira em'} {formatDateTimeBR(estatuto.expires_at)}
              </Text>
            </Flex>
          )}
          {estatuto.dt_preenchimento && (
            <Flex align="center" gap={1.5}>
              <Clock size={12} color="#16A34A" />
              <Text fontSize="10px" color="green.600">
                Preenchido em {formatDateTimeBR(estatuto.dt_preenchimento)}
              </Text>
            </Flex>
          )}
        </Flex>
      )}

      {/* Footer: responsáveis/supervisores (T4) + anexos (T5). Dados já chegam no card. */}
      {hasFooter && (
        <Flex
          align="center"
          justify="space-between"
          mt={2}
          pt={2}
          borderTop="1px solid"
          borderColor="gray.50"
        >
          <Flex align="center" gap={2}>
            {responsaveis.length > 0 && (
              <AvatarGroup size="xs" max={3} spacing="-5px">
                {responsaveis.map(u => (
                  <Tooltip key={u.id} label={u.nome || 'Responsável'} placement="top" hasArrow>
                    <Avatar name={u.nome || 'R'} size="xs" />
                  </Tooltip>
                ))}
              </AvatarGroup>
            )}
            {supervisores.length > 0 && (
              <AvatarGroup size="xs" max={3} spacing="-5px">
                {supervisores.map(s => (
                  <Tooltip
                    key={s.id}
                    label={`${s.nome || 'Usuário'} (Supervisor)`}
                    placement="top"
                    hasArrow
                  >
                    <Avatar
                      name={s.nome || 'S'}
                      size="xs"
                      border="2px solid"
                      borderColor="purple.400"
                    />
                  </Tooltip>
                ))}
              </AvatarGroup>
            )}
          </Flex>
          {anexosCount > 0 && (
            <Flex align="center" gap={1} color="gray.400">
              <Paperclip size={12} />
              <Text fontSize="10px" fontWeight="600">
                {anexosCount}
              </Text>
            </Flex>
          )}
        </Flex>
      )}
    </Box>
  );
};
